/**
 * AdminGymRequestReviewPage Component
 *
 * Review panel for a single gym request.
 * Allows admins to approve, reject, or request changes.
 */

import { useState, useEffect, type CSSProperties, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { GymRequestStatusBadge } from '@/components/gym-request/GymRequestStatusBadge';
import type { GymRequest, GymRequestStatus } from '@flash/shared';

/**
 * Review action types
 */
type ReviewAction = 'approve' | 'reject' | 'request_changes';

/**
 * Response type from reviewGymRequest callable
 */
interface ReviewGymRequestResponse {
  success: boolean;
  gymId?: string;
}

/**
 * Convert Firestore document to GymRequest type
 */
const docToGymRequest = (docId: string, docData: DocumentData): GymRequest => {
  return {
    id: docId,
    requesterId: docData['requesterId'] as string,
    status: docData['status'] as GymRequestStatus,
    name: docData['name'] as string,
    description: docData['description'] as string,
    address: docData['address'] as string,
    city: docData['city'] as string,
    state: docData['state'] as string,
    country: docData['country'] as string,
    adminNotes: (docData['adminNotes'] as string | null) ?? null,
    reviewedBy: (docData['reviewedBy'] as string | null) ?? null,
    reviewedAt: docData['reviewedAt'] ?? null,
    gymId: (docData['gymId'] as string | null) ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * Format a timestamp for display
 */
const formatDate = (timestamp: unknown): string => {
  if (!timestamp) {
    return 'Unknown date';
  }

  // Handle Firestore Timestamp
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    const date = (timestamp as { toDate: () => Date }).toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return 'Unknown date';
};

/**
 * AdminGymRequestReviewPage displays a single request for admin review
 *
 * @example
 * ```tsx
 * // In router (wrapped in AdminRoute)
 * <Route path="/admin/gym-requests/:id" element={<AdminRoute><AdminGymRequestReviewPage /></AdminRoute>} />
 * ```
 */
export const AdminGymRequestReviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [request, setRequest] = useState<GymRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Review form state
  const [selectedAction, setSelectedAction] = useState<ReviewAction | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch request data with real-time updates
  useEffect(() => {
    if (!id || !user) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    const requestRef = doc(db, 'gymRequests', id);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      requestRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const requestData = docToGymRequest(docSnapshot.id, docSnapshot.data());
          setRequest(requestData);
          setError(null);
        } else {
          setError('Gym request not found.');
          setRequest(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching gym request:', err);
        setError('Failed to load gym request. Please try again.');
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [id, user]);

  /**
   * Handle review submission
   */
  const handleSubmitReview = async (e: FormEvent) => {
    e.preventDefault();

    if (!selectedAction || !request) {
      return;
    }

    // Validate notes for reject/request_changes
    if (selectedAction !== 'approve' && notes.trim().length < 10) {
      setSubmitError('Notes are required for reject or request changes (min 10 characters)');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const functions = getFirebaseFunctions();
      const reviewGymRequest = httpsCallable<
        { requestId: string; action: ReviewAction; notes?: string },
        ReviewGymRequestResponse
      >(functions, 'reviewGymRequest');

      const trimmedNotes = notes.trim();
      const requestData = trimmedNotes
        ? { requestId: request.id, action: selectedAction, notes: trimmedNotes }
        : { requestId: request.id, action: selectedAction };

      const result = await reviewGymRequest(requestData);

      if (result.data.success) {
        // Navigate back to requests list on success
        navigate('/admin/gym-requests');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit review';
      console.error('Error submitting review:', err);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset the review form
   */
  const handleResetForm = () => {
    setSelectedAction(null);
    setNotes('');
    setSubmitError(null);
  };

  // Check if request can be reviewed
  const canReview = request?.status === 'pending' || request?.status === 'changes_requested';

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading request details...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error</h2>
            <p style={errorTextStyle}>{error}</p>
          </div>
        </div>
      </main>
    );
  }

  if (!request) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Request Not Found</h2>
            <p style={errorTextStyle}>The gym request you are looking for does not exist.</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <div style={headerMainStyle}>
            <div style={titleContainerStyle}>
              <h1 style={titleStyle}>{request.name}</h1>
              <GymRequestStatusBadge status={request.status} style={statusBadgeStyle} />
            </div>
            <p style={locationStyle}>
              {request.city}, {request.state}, {request.country}
            </p>
          </div>
        </header>

        {/* Already Reviewed Banner */}
        {!canReview && (
          <div style={alreadyReviewedStyle}>
            <h3 style={alreadyReviewedTitleStyle}>Already Reviewed</h3>
            <p style={alreadyReviewedTextStyle}>
              This request has already been {request.status.replace('_', ' ')}.
              {request.gymId && request.status === 'approved' && (
                <>
                  {' '}
                  <Link to={`/gyms/${request.gymId}`} style={viewGymLinkStyle}>
                    View the gym
                  </Link>
                </>
              )}
            </p>
          </div>
        )}

        {/* Request Details */}
        <div style={detailsContainerStyle}>
          <h2 style={sectionTitleStyle}>Request Details</h2>

          <div style={detailSectionStyle}>
            <h3 style={detailLabelStyle}>Description</h3>
            <p style={detailValueStyle}>{request.description}</p>
          </div>

          <div style={detailSectionStyle}>
            <h3 style={detailLabelStyle}>Address</h3>
            <p style={detailValueStyle}>{request.address}</p>
            <p style={detailValueStyle}>
              {request.city}, {request.state}
            </p>
            <p style={detailValueStyle}>{request.country}</p>
          </div>

          <div style={metaContainerStyle}>
            <div style={metaItemStyle}>
              <span style={metaLabelStyle}>Submitted</span>
              <span style={metaValueStyle}>{formatDate(request.createdAt)}</span>
            </div>
            <div style={metaItemStyle}>
              <span style={metaLabelStyle}>Requester ID</span>
              <span style={metaValueStyle}>{request.requesterId}</span>
            </div>
          </div>
        </div>

        {/* Previous Admin Notes */}
        {request.adminNotes && (
          <div style={previousNotesStyle}>
            <h2 style={sectionTitleStyle}>Previous Admin Feedback</h2>
            <p style={previousNotesTextStyle}>{request.adminNotes}</p>
            {request.reviewedAt && (
              <p style={reviewedAtStyle}>Reviewed: {formatDate(request.reviewedAt)}</p>
            )}
          </div>
        )}

        {/* Review Form */}
        {canReview && (
          <div style={reviewFormContainerStyle}>
            <h2 style={sectionTitleStyle}>Review Decision</h2>

            <form onSubmit={handleSubmitReview}>
              {/* Action Buttons */}
              <div style={actionsContainerStyle}>
                <button
                  type="button"
                  onClick={() => setSelectedAction('approve')}
                  disabled={isSubmitting}
                  style={{
                    ...actionButtonStyle,
                    ...approveButtonStyle,
                    ...(selectedAction === 'approve' ? selectedApproveStyle : {}),
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAction('request_changes')}
                  disabled={isSubmitting}
                  style={{
                    ...actionButtonStyle,
                    ...changesButtonStyle,
                    ...(selectedAction === 'request_changes' ? selectedChangesStyle : {}),
                  }}
                >
                  Request Changes
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAction('reject')}
                  disabled={isSubmitting}
                  style={{
                    ...actionButtonStyle,
                    ...rejectButtonStyle,
                    ...(selectedAction === 'reject' ? selectedRejectStyle : {}),
                  }}
                >
                  Reject
                </button>
              </div>

              {/* Notes Textarea */}
              {selectedAction && (
                <div style={notesContainerStyle}>
                  <label htmlFor="notes" style={notesLabelStyle}>
                    Admin Notes{' '}
                    {selectedAction !== 'approve' && (
                      <span style={requiredStyle}>(Required - min 10 characters)</span>
                    )}
                  </label>
                  <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={
                      selectedAction === 'approve'
                        ? 'Optional notes for the requester...'
                        : 'Explain why changes are needed or why the request is rejected...'
                    }
                    disabled={isSubmitting}
                    style={notesTextareaStyle}
                    rows={4}
                  />
                </div>
              )}

              {/* Error Message */}
              {submitError && (
                <div style={submitErrorStyle}>
                  <p style={submitErrorTextStyle}>{submitError}</p>
                </div>
              )}

              {/* Submit/Cancel Buttons */}
              {selectedAction && (
                <div style={formActionsStyle}>
                  <button
                    type="button"
                    onClick={handleResetForm}
                    disabled={isSubmitting}
                    style={cancelButtonStyle}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      ...submitButtonStyle,
                      ...(selectedAction === 'approve'
                        ? submitApproveStyle
                        : selectedAction === 'reject'
                          ? submitRejectStyle
                          : submitChangesStyle),
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : `Confirm ${selectedAction.replace('_', ' ')}`}
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
};

const contentStyle: CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: '#6b7280',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  marginBottom: '16px',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const headerMainStyle: CSSProperties = {};

const titleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '8px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1f2937',
  margin: 0,
};

const statusBadgeStyle: CSSProperties = {
  fontSize: '0.875rem',
  padding: '6px 14px',
};

const locationStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  margin: 0,
};

const alreadyReviewedStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '16px',
  backgroundColor: '#f3f4f6',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  marginBottom: '24px',
};

const alreadyReviewedTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#374151',
  margin: 0,
};

const alreadyReviewedTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#6b7280',
  margin: 0,
};

const viewGymLinkStyle: CSSProperties = {
  color: '#3b82f6',
  fontWeight: 500,
  textDecoration: 'underline',
};

const detailsContainerStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
  marginBottom: '24px',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1f2937',
  margin: '0 0 16px 0',
};

const detailSectionStyle: CSSProperties = {
  marginBottom: '20px',
};

const detailLabelStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '0 0 6px 0',
};

const detailValueStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#1f2937',
  lineHeight: 1.6,
  margin: 0,
};

const metaContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '24px',
  flexWrap: 'wrap',
  paddingTop: '20px',
  borderTop: '1px solid #e5e7eb',
  marginTop: '20px',
};

const metaItemStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const metaLabelStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const metaValueStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#374151',
};

const previousNotesStyle: CSSProperties = {
  backgroundColor: '#fef3c7',
  border: '1px solid #fde68a',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '24px',
};

const previousNotesTextStyle: CSSProperties = {
  fontSize: '0.9375rem',
  color: '#78350f',
  lineHeight: 1.6,
  margin: 0,
  whiteSpace: 'pre-wrap',
};

const reviewedAtStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: '#92400e',
  marginTop: '12px',
  marginBottom: 0,
};

const reviewFormContainerStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
};

const actionsContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '20px',
};

const actionButtonStyle: CSSProperties = {
  flex: 1,
  minWidth: '120px',
  padding: '14px 20px',
  border: '2px solid',
  borderRadius: '8px',
  fontSize: '0.9375rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const approveButtonStyle: CSSProperties = {
  backgroundColor: 'white',
  borderColor: '#10b981',
  color: '#059669',
};

const selectedApproveStyle: CSSProperties = {
  backgroundColor: '#10b981',
  color: 'white',
};

const changesButtonStyle: CSSProperties = {
  backgroundColor: 'white',
  borderColor: '#f59e0b',
  color: '#d97706',
};

const selectedChangesStyle: CSSProperties = {
  backgroundColor: '#f59e0b',
  color: 'white',
};

const rejectButtonStyle: CSSProperties = {
  backgroundColor: 'white',
  borderColor: '#ef4444',
  color: '#dc2626',
};

const selectedRejectStyle: CSSProperties = {
  backgroundColor: '#ef4444',
  color: 'white',
};

const notesContainerStyle: CSSProperties = {
  marginBottom: '20px',
};

const notesLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '8px',
};

const requiredStyle: CSSProperties = {
  color: '#dc2626',
  fontWeight: 400,
};

const notesTextareaStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '0.9375rem',
  lineHeight: 1.5,
  resize: 'vertical',
  boxSizing: 'border-box',
};

const submitErrorStyle: CSSProperties = {
  padding: '12px',
  backgroundColor: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  marginBottom: '16px',
};

const submitErrorTextStyle: CSSProperties = {
  color: '#991b1b',
  fontSize: '0.875rem',
  margin: 0,
};

const formActionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
};

const cancelButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: '#6b7280',
  cursor: 'pointer',
};

const submitButtonStyle: CSSProperties = {
  padding: '12px 24px',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: 'white',
  cursor: 'pointer',
};

const submitApproveStyle: CSSProperties = {
  backgroundColor: '#10b981',
};

const submitChangesStyle: CSSProperties = {
  backgroundColor: '#f59e0b',
};

const submitRejectStyle: CSSProperties = {
  backgroundColor: '#ef4444',
};

export default AdminGymRequestReviewPage;
